import type { ReactNode } from 'react';
import { useTranslations } from 'use-intl';
import { useAiStatus } from '@/app/queries';
import { Button } from '@/ui/Button';
import { Notice } from '@/ui/Panel';

/**
 * The thing every assistant is behind: one button, and the balance beside it.
 *
 * The balance is shown before the call rather than only after, because a coach
 * about to spend credits should be able to see what he has without opening
 * another screen — and because an assistant that charges silently is one he
 * stops trusting the first time he notices.
 *
 * When the club cannot use the assistants at all, this renders why instead of a
 * dead button. A disabled control with no explanation is a support ticket.
 */
export function AiButton({
  label,
  busy,
  disabled,
  onRun,
  children,
}: {
  label: string;
  busy: boolean;
  disabled?: boolean;
  onRun: () => void;
  children?: ReactNode;
}) {
  const t = useTranslations();
  const status = useAiStatus();
  if (!status.data) return null;
  if (!status.data.enabled || !status.data.inPlan) {
    return (
      <Notice>
        {status.data.inPlan ? t('ai.refused.ai_switched_off') : t('ai.refused.ai_not_in_plan')}
      </Notice>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button type="button" variant="secondary" busy={busy} disabled={disabled} onClick={onRun}>
        {label}
      </Button>
      <span className="tabular text-meta text-ink-faint">
        {t('ai.balanceLeft', { count: status.data.credits })}
      </span>
      {children}
    </div>
  );
}
